<template>
    <div class="create-edit-article">
        <div class="create-edit-article__background">
            <img src="~@/assets/images/dashboard-admin.png" alt="kasanowa" />
        </div>
        <KLayoutWrapper>
            <template #default>
                <div>
                    <div class="create-edit-article__wrapper">
                        <div class="create-edit-article__title">{{ router.currentRoute.value.params.id === 'create' ? $t('articles.createArticle') : $t('articles.editArticle') }}</div>
                        <div class="create-edit-article__close-wrapper kasanowa-hover" @click="navigateTo('/articles')">
                            <KIcon icon="close" :color="black" width="25" height="25" />
                        </div>
                        <KLoader v-show="loadingArticleDetails" />
                        <Form v-show="!loadingArticleDetails" :validation-schema="schema" @submit="onSubmit" class="create-edit-article__form-wrapper">
                            <div class="create-edit-article__input-wrapper">
                                <KTextField
                                    id="input-title"
                                    v-model="articleForm.title"
                                    name="title"
                                    label=""
                                    :placeholder="$t('articles.titleForm')"
                                />
                                <KTextField
                                    id="input-lead"
                                    v-model="articleForm.lead"
                                    name="lead"
                                    label=""
                                    :placeholder="$t('articles.leadForm')"
                                />
                                <KAutocomplete
                                    id="select-product"
                                    v-model="articleForm.product_id"
                                    name="selectProduct"
                                    :get-items="productsList"
                                    item-title="name"
                                    item-value="id"
                                    label=""
                                    :placeholder="$t('articles.product')"
                                    variant="outlined"
                                    clearable
                                    hide-validation
                                />
                                <KVueEditor
                                    id="article-content"
                                    v-model="articleForm.content"
                                    name="content"
                                />
                                <div v-if="articleForm.image == null || typeof articleForm.image === 'object'">
                                    <DragAndDropFile
                                        input-id="drop-image"
                                        acceptance=".png,.jpg,.jpeg"
                                        :on-files-change="onFilesChange"
                                    />
                                </div>
                                <div v-else class="create-edit-article__img-wrapper">
                                    <img :src="articleForm.image" alt="kasanowa" />
                                    <div class="create-edit-article__hover-input-wrapper">
                                        <KButton
                                            id="change-image-btn"
                                            :label="$t('articles.change')"
                                            type="submit"
                                            secondary
                                            @click="handleChangeImage"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div class="create-edit-article__btn-wrapper">
                                <KButton
                                    id="submit-btn"
                                    :label="$t('articles.save')"
                                    type="submit"
                                    icon="arrowRight"
                                    :color="black"
                                    width="11"
                                    height="11"
                                    :disabled="loading"
                                    :loading="loading"
                                />
                            </div>
                        </Form>
                    </div>
                </div>
            </template>
        </KLayoutWrapper>
    </div>
</template>

<script setup lang="ts">
import { ref } from '@vue/runtime-core';
import router from '@/router';
import { Form } from 'vee-validate';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user';
import * as yup from 'yup';
import ArticleApi from '@/api/v1/ArticleApi';
import ProductApi from '@/api/v1/ProductApi';
import { MAX_SIZE_LIMIT } from '@/hooks/GlobalHooks';
import { toast } from 'vue3-toastify';
import { navigateTo, navigateToWithSuccessToast, navigateToWithErrorToast } from '@/hooks/NavigateHooks';
import variables from '@/assets/scss/modules/variables.module.scss';
import type { IArticleForm } from '@/models/ArticleModel';
import type { IProductList } from '@/models/ProductsModel';
import KLayoutWrapper from '@/components/atoms/KLayoutWrapper.vue';
import KTextField from '@/components/atoms/KTextField.vue';
import KButton from '@/components/atoms/KButton.vue';
import KLoader from '@/components/atoms/KLoader.vue';
import KIcon from '@/components/atoms/KIcon.vue';
import KVueEditor from '@/components/atoms/KVueEditor.vue';
import DragAndDropFile from '@/components/molecules/DragAndDropFile.vue';
import KAutocomplete from '@/components/atoms/KAutocomplete.vue';

const { black } = variables;

const articleForm = ref<IArticleForm>({
    id: null,
    title: null,
    lead: null,
    content: null,
    image: null,
    product_id: null,
});
const schema:any = yup.object({
    title: yup.string().required(`${t('articles.validation.titleRequired')}`).label('title'),
    lead: yup.string().required(`${t('articles.validation.leadRequired')}`).label('lead'),
    content: yup.string().required(`${t('articles.validation.contentRequired')}`).label('content').test('content', `${t('articles.validation.contentTooLong')}`,
    function (val) {
        if (!val) return true;

        const contentSizeInBytes = new Blob([val]).size;

        if (contentSizeInBytes <= MAX_SIZE_LIMIT) {
            return true;
        } else {
            return false;
        }
    }),
});
const onFilesChange = (files: File[]) => {
    if (null != files && files.length > 0) {
        articleForm.value.image = files[0];
    } else {
        articleForm.value.image = null;
    }
};
const loading = ref<boolean>(false);
const articleApi = new ArticleApi();
const { isAdmin, isAdminColian } = storeToRefs(useUserStore());
const onSubmit = async() => {
    if (articleForm.value.image == null) {
        toast.error(t('articles.validation.imageRequire'));
        return;
    }
    loading.value = true;
    if (router.currentRoute.value.params.id === 'create') {
        try {
            const result = await articleApi.createArticle({
                title: articleForm.value.title,
                excerpt: articleForm.value.lead,
                content: articleForm.value.content,
                image: articleForm.value.image,
                product_id: articleForm.value.product_id,
            },
                isAdmin.value || isAdminColian.value
            );
            if (!result.isSuccess) {
                toast.error(t('articles.toast.errorCreateArticle'));
                loading.value = false;
                return;
            }
            navigateToWithSuccessToast('/articles', t('articles.toast.successCreateArticle'));
        } catch (e) {
            toast.error(t('articles.toast.errorCreateArticle'));
            loading.value = false;
            return;
        }
    } else {
        try {
            const result = await articleApi.editArticle(
                {
                    id: articleForm.value.id,
                    title: articleForm.value.title,
                    excerpt: articleForm.value.lead,
                    content: articleForm.value.content,
                    image: articleForm.value.image,
                    product_id: articleForm.value.product_id,
                },
                router.currentRoute.value.params.id,
                isAdmin.value || isAdminColian.value,
            );
            if (!result.isSuccess) {
                toast.error(t('articles.toast.errorEditArticle'));
                loading.value = false;
                return;
            }
            navigateToWithSuccessToast('/articles', t('articles.toast.successEditArticle'));
        } catch (e) {
            toast.error(t('articles.toast.errorEditArticle'));
            loading.value = false;
            return;
        }
    }
    loading.value = false;
};

const loadingArticleDetails = ref<boolean>(false);
const articlesDetails = async() => {
    loadingArticleDetails.value = true;
    try {
        const result = await articleApi.articleDetails(router.currentRoute.value.params.id, isAdmin.value || isAdminColian.value);
        if (!result.isSuccess) {
            navigateToWithErrorToast('/articles', t('articles.toast.errorArticleDetails'));
            loadingArticleDetails.value = false;
            return;
        }
        articleForm.value.id = result.payload.id;
        articleForm.value.title = result.payload.name;
        articleForm.value.lead = result.payload.lead;
        articleForm.value.content = result.payload.content;
        articleForm.value.image = result.payload.image;
        articleForm.value.product_id = result.payload.product_id;
    } catch (e) {
        navigateToWithErrorToast('/articles', t('articles.toast.errorArticleDetails'))
        loadingArticleDetails.value = false;
        return;
    }
    loadingArticleDetails.value = false;
};
if (router.currentRoute.value.params.id !== 'create') {
    articlesDetails();
}

const handleChangeImage = () => {
    articleForm.value.image = null;
};

const productsList = ref<IProductList[]>([]);
const productApi = new ProductApi();
const getProductsList = async() => {
    try {
        const result = await productApi.productsList();
        if (!result.isSuccess) {
            return;
        }
        productsList.value = result.payload.products;
    } catch (e) {
        return;
    }
};
getProductsList();
</script>

<style scoped lang="scss">
.create-edit-article {
    position: relative;
    width: 100%;

    &__background {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 0;
        width: 100%;
        height: 100%;

        img {
            object-fit: cover;
            width: 100%;
            height: calc(100% - 184px);
            min-height: calc(100vh - 184px);

            @include media-breakpoint-up(lg) {
                height: calc(100% - 217px);
                min-height: calc(100vh - 217px);
            }
        }
    }

    &__wrapper {
        position: relative;
        background: rgba($white, 0.15);
        backdrop-filter: blur(30px) saturate(150%);
        -webkit-backdrop-filter: blur(30px) saturate(150%);
        border-radius: 5px;
        border: 1px solid $secondary-400;
        padding: 45px 15px 15px 15px;
        max-width: 1025px;
        margin: 20px auto 0;

        @include media-breakpoint-up(lg) {
            padding: 124px 52px 60px 52px;
            margin: 50px auto 0;
        }
    }

    &__title {
        width: 90%;
        position: absolute;
        top: -22px;
        left: 50%;
        transform: translateX(-50%);
        background-color: $primary-400;
        text-transform: uppercase;
        font-weight: 900;
        letter-spacing: 0;
        text-align: center;
        padding: 10px;

        @include media-breakpoint-up(lg) {
            font-size: 52px;
            padding: 9px;
            top: -50px;
        }
    }

    &__form-wrapper {
        display: flex;
        flex-direction: column;
    }

    &__input-wrapper {
        display: flex;
        flex-direction: column;

        &:deep(.k-input .v-field__input) {
            background-color: $white;
        }

        &:deep(.k-vue-editor .ql-editor) {
            background-color: $white;
            border-top-right-radius: 0;
            border-top-left-radius: 0;
        }

        &:deep(.k-vue-editor .ql-toolbar) {
            background-color: $white;
        }

        &:deep(input::placeholder) {
            opacity: 0.9;
        }
    }

    &__btn-wrapper {
        width: 100%;
        display: flex;
        flex-direction: column;

        @include media-breakpoint-up(lg) {
            flex-direction: row;
            justify-content: flex-end;
        }
    }

    &__close-wrapper {
        position: absolute;
        top: 18px;
        right: 12px;

        @include media-breakpoint-up(sm) {
            top: 12px;
            right: 12px;
        }
    }

    &__img-wrapper {
        width: 100%;
        height: 100%;
        position: relative;

        img {
            border-radius: 8px;
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        &:hover {
            .create-edit-article__hover-input-wrapper {
                display: flex;
            }
        }
    }

    &__hover-input-wrapper {
        display: none;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    &:deep(.drag-and-drop-file__drop-wrapper--input-wrapper) {
        background-color: $white;
        border-radius: 5px;
    }

    &:deep(.k-autocomplete) {
        background-color: $white;
        border-radius: 8px;
        margin-bottom: 20px;
    }
}
</style>
